'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {Title as DialogTitle,Description as DialogDescription} from '@radix-ui/react-dialog';
import { isAxiosError } from 'axios';
import { Button, ContentDialog, TextArea, cn, toast } from '@crabs-ai/ds-react';
import { Settings } from '@crabs-ai/web-icons';
import { PlayerRef } from '@remotion/player';
import { useLocale, useTranslations } from 'next-intl';
import { useGetTTSVoicesApi } from '@/shared/apis/tts/get-tts-voices';
import {useVoiceDraftStage} from '@waka/compat/voice-draft';
import { TTSVoice } from '@/shared/components/dialog/TtsDialog/TtsDialogContext';
import TtsDialog from '@/shared/components/dialog/TtsDialog/TtsDialog';
import { formatTtsDisplayName } from '@/shared/lib/format-tts-display-name';
import {
  DEFAULT_EMOTION,
  DEFAULT_PITCH,
  DEFAULT_TEMPO,
} from '@/shared/constants/default-voice';
import { DtoEmotion, DtoProjectType, DtoVoice } from '@/shared/constants';
import { addAsset } from '@/features/remotion-editor/editor/assets/add-asset-wkwk';
import { classifyUploadFailure } from '@/shared/utils/upload-error-classifier';
import { showUploadFailureToast } from '@/shared/utils/upload-failure-toast';
import { generateIdempotencyKey } from '@/shared/utils/generate-idempotency-key';
import { isTtsBlockedError } from '@/shared/lib/tts-blocked-error';
import {
  useCurrentStateAsRef,
  useWriteContext,
} from '@/features/remotion-editor/editor/utils/use-context';
import { CLIP_TYPES } from '@/shared/apis/editor/clip-types';
import { useGetSpaceMeApi } from '@/features/my-space/api/get-space-me';
import { isHighQualityTtsBlockedOnPlan } from '@/shared/user-plan/user-spec';
import { useProjectTypeContext } from '@/features/remotion-editor/editor/contexts/project-type-context';
import { pickDefaultTtsCatalogVoice } from '@/shared/lib/tts-catalog-defaults';

interface AiVoiceGenerateDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  playerRef: React.RefObject<PlayerRef | null>;
}

export default function AiVoiceGenerateDialog({
  open,
  onClose,
  projectId,
  playerRef,
}: AiVoiceGenerateDialogProps) {
  const t = useTranslations();
  const locale = useLocale();
  const { data: voiceData } = useGetTTSVoicesApi();
  const { data: spaceData } = useGetSpaceMeApi();
  const { mutateAsync, isPending } = useVoiceDraftStage();
  const { projectType } = useProjectTypeContext();
  const timelineWriteContext = useWriteContext();
  const stateAsRef = useCurrentStateAsRef();

  const [selectedTtsVoice, setSelectedTtsVoice] = useState<TTSVoice | null>(
    null,
  );
  const [currentText, setCurrentText] = useState('');
  const [isTtsDialogOpen, setIsTtsDialogOpen] = useState(false);

  const findDefaultVoice = useMemo(() => {
    return voiceData?.data ? pickDefaultTtsCatalogVoice(voiceData.data) : null;
  }, [voiceData]);

  useEffect(() => {
    if (voiceData?.data && !selectedTtsVoice && findDefaultVoice) {
      const defaultEmotion = findDefaultVoice.emotions.find(
        (e) => e.code === DEFAULT_EMOTION,
      );
      if (defaultEmotion) {
        setSelectedTtsVoice({
          voice: findDefaultVoice,
          tempo: DEFAULT_TEMPO,
          pitch: DEFAULT_PITCH,
          emotion: defaultEmotion,
        });
      }
    }
  }, [voiceData, selectedTtsVoice, findDefaultVoice]);

  const isPlanBlockingHighQuality = useMemo(() => {
    if (!spaceData?.data?.planId) return false;
    return isHighQualityTtsBlockedOnPlan(spaceData.data.planId);
  }, [spaceData?.data?.planId]);

  const handleGenerate = useCallback(async () => {
    try {
      if (!currentText.trim()) {
        toast({
          type: 'error',
          description: t('Editor.ai-voice-panel.empty-text-error'),
          toastOptions: { position: 'top-center' },
        });
        return;
      }

      if (!selectedTtsVoice?.voice?.code || !selectedTtsVoice?.emotion?.code) {
        return;
      }

      const res = await mutateAsync({
        projectId,
        idempotencyKey: generateIdempotencyKey(),
        text: currentText,
        voice: selectedTtsVoice.voice.code as DtoVoice,
        speedFactor: selectedTtsVoice.tempo ?? DEFAULT_TEMPO,
        pitch: selectedTtsVoice.pitch ?? DEFAULT_PITCH,
        emotion: selectedTtsVoice.emotion.code as DtoEmotion,
        style: selectedTtsVoice.style, // Gemini 전용, 다른 벤더는 무시
        from:playerRef.current?.getCurrentFrame()??0,
        fps:stateAsRef.current.undoableState.fps,
      });

      if(res.awaitingConfirmation){onClose();return;}

      const assetData = res.data;
      if (!assetData) {
        return;
      }

      try {
        const audioResponse = await fetch(assetData.publicUrl);
        if (!audioResponse.ok) {
          throw new Error(`HTTP error! status: ${audioResponse.status}`);
        }
        const audioBlob = await audioResponse.blob();
        const state = stateAsRef.current;

        await addAsset({
          file: audioBlob,
          contentId: assetData.contentId.toString(),
          filename: assetData.name,
          clip: {
            clipType: CLIP_TYPES.AUDIO,
            contentPublicUri: assetData.publicUrl,
            videoStart: 0,
            videoEnd: assetData.duration,
          },
          timelineWriteContext,
          playerRef,
          dropPosition: null,
          fps: state.undoableState.fps,
          compositionWidth: state.undoableState.compositionWidth,
          compositionHeight: state.undoableState.compositionHeight,
          tracks: state.undoableState.tracks,
          projectType: projectType ?? DtoProjectType.spotlight,
          isAsset: true,
          extension: 'mp3',
        });
      } catch (fetchError) {
        console.error('[AiVoiceGenerateDialog] addAsset failed:', fetchError);
        showUploadFailureToast(
          t,
          classifyUploadFailure(
            fetchError,
            assetData.name ?? t('Common.upload-fallback-name'),
          ),
        );
      }

      toast({
        type: 'success',
        description: t('Editor.ai-voice-panel.generate-success'),
        toastOptions: { position: 'top-center' },
      });

      setCurrentText('');
      onClose();
    } catch (error) {
      console.error('handleGenerate error', error);
      // 422 정책 차단 (1031/1032) 은 전역 TtsBlockedDialog 가 흡수 — 추가 toast skip.
      if (isTtsBlockedError(error)) return;
      // 상태별 사용자 피드백 — ScriptContentSceneCard 의 handleRegenerate 와 동일 패턴:
      //  - 409 Conflict: 멱등성 락 중복 — "이미 진행 중" 안내
      //  - 402 (포인트 부족, code=1301): coin-provider 전역 interceptor 가 CoinShortageDialog 표시 — 여기서 중복 처리 금지
      //  - 400 (validation): FE/BE 계약 위반 (개발자 이슈) — 콘솔 로그만
      //  - 기타: 일반 실패 toast
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 409) {
          toast({
            type: 'error',
            description: t('Editor.ai-voice-panel.in-progress'),
            toastOptions: { position: 'top-center' },
          });
        } else if (status !== 402 && status !== 400) {
          toast({
            type: 'error',
            description: t('Editor.ai-voice-panel.generate-error'),
            toastOptions: { position: 'top-center' },
          });
        }
      } else {
        // axios 가 아닌 예외 (로컬 코드 버그) 도 최소한의 사용자 피드백.
        toast({
          type: 'error',
          description: t('Editor.ai-voice-panel.generate-error'),
          toastOptions: { position: 'top-center' },
        });
      }
    }
  }, [
    currentText,
    mutateAsync,
    onClose,
    playerRef,
    projectId,
    projectType,
    selectedTtsVoice,
    stateAsRef,
    t,
    timelineWriteContext,
  ]);

  return (
    <>
      <ContentDialog
        open={open}
        setOpen={(nextOpen) => {
          if (!nextOpen) {
            onClose();
          }
        }}
        isShowHeaderNode={false}
        isDefaultFooterButtonsHidden
      >
        <div className="flex flex-col gap-xs">
          <div className="flex flex-col gap-4xs">
            <DialogTitle className="text-title-large text-sys-cont-neutral-default">
              {t('Editor.ai-source.action.audio')}
            </DialogTitle>
            <DialogDescription className="text-body-medium text-sys-cont-neutral-default">
              {t('Editor.ai-source.audio-dialog.description')}
            </DialogDescription>
          </div>

          <div className="flex flex-col gap-4xs">
            <p className="text-title-xsmall text-sys-cont-neutral-default">
              {t('Editor.ai-source.audio-dialog.voice-label')}
            </p>
            <button
              type="button"
              aria-label={t('Editor.script-panel.settings')}
              className={cn(
                'h-9 w-full cursor-pointer rounded-2xs px-xs py-3xs transition-all outline-none',
                'bg-sys-style-neutral-lightest data-[disabled=false]:hover:bg-sys-style-neutral-light data-[disabled=false]:active:bg-sys-style-neutral-default',
                isPending && 'cursor-wait opacity-40',
              )}
              disabled={isPending}
              data-disabled={isPending ? 'true' : 'false'}
              onClick={() => setIsTtsDialogOpen(true)}
            >
              <div className="flex items-center justify-between gap-4xs">
                <span className="flex-1 text-start text-comp-moderate-medium text-sys-cont-neutral-default">
                  {formatTtsDisplayName(selectedTtsVoice, locale, '-')}
                </span>
                <Settings className="size-4 [&_path]:fill-sys-cont-neutral-default" />
              </div>
            </button>
          </div>

          <div className="flex flex-col gap-4xs">
            <p className="text-title-xsmall text-sys-cont-neutral-default">
              {t('Editor.ai-source.audio-dialog.content-label')}
            </p>
            <TextArea
              placeholder={t('Editor.ai-voice-panel.placeholder')}
              state={isPending ? 'disabled' : 'default'}
              value={currentText}
              onChange={(e) => setCurrentText(e.target.value)}
              isShowLabel={false}
              className="min-h-7xl"
            />
          </div>

          <div className="flex items-center justify-end gap-2xs">
            <Button
              className="w-fit shrink-0"
              hierarchy="tertiary"
              size="small"
              onClick={onClose}
            >
              {t('Common.cancel')}
            </Button>
            <Button
              className="w-fit shrink-0"
              hierarchy="primary"
              size="small"
              state={isPending ? 'loading' : 'default'}
              onClick={handleGenerate}
              disabled={!currentText.trim()}
            >
              {isPending?'편집본 저장 중…':'저장하고 생성 내용 확인'}
            </Button>
          </div>
        </div>
      </ContentDialog>

      {isTtsDialogOpen && (
        <TtsDialog
          open={isTtsDialogOpen}
          showPlanWarning={isPlanBlockingHighQuality}
          planId={spaceData?.data?.planId ?? undefined}
          setOpen={() => setIsTtsDialogOpen(false)}
          onConfirm={setSelectedTtsVoice}
          defaultTts={selectedTtsVoice}
        />
      )}
    </>
  );
}
